import { usePathname } from 'next/navigation'
import {
  createContext,
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useContext,
  useEffect,
} from 'react'
import type { GlobalPageTransitionFns, PageTransitionFns } from './types'

type PageTransitionContextType = {
  transitionFns: PageTransitionFns
  setTransitionFns: Dispatch<SetStateAction<PageTransitionFns>>
  globalTransitionFns: GlobalPageTransitionFns
  setGlobalTransitionFns: Dispatch<SetStateAction<GlobalPageTransitionFns>>
  navigate: (href: string) => void
  elementRefs: RefObject<Record<string, RefObject<unknown>>>
  globalElementRefs: RefObject<Record<string, RefObject<unknown>>>
  setGlobalTransitionPages: Dispatch<SetStateAction<string[]>>
}

export const defaultPageTransitionFns: PageTransitionFns = {
  enter: async () => new Promise((resolve) => resolve(true)),
  exit: async () => new Promise((resolve) => resolve(true)),
}

export const defaultGlobalPageTransitionFns: GlobalPageTransitionFns = {
  onBeforeEnter: async () => new Promise((resolve) => resolve(true)),
  onAfterEnter: async () => new Promise((resolve) => resolve(true)),
  onBeforeExit: async () => new Promise((resolve) => resolve(true)),
  onAfterExit: async () => new Promise((resolve) => resolve(true)),
}

export const PageTransitionContext = createContext<PageTransitionContextType>({
  transitionFns: defaultPageTransitionFns,
  setTransitionFns: () => {
    // Default implementation - will be overridden by provider
  },
  globalTransitionFns: defaultGlobalPageTransitionFns,
  setGlobalTransitionFns: () => {
    // Default implementation - will be overridden by provider
  },
  navigate: () => {
    // Default implementation - will be overridden by provider
  },
  elementRefs: { current: {} },
  globalElementRefs: { current: {} },
  setGlobalTransitionPages: () => {
    // Default implementation - will be overridden by provider
  },
})

export function usePageTransition({ enter, exit }: PageTransitionFns) {
  const { setTransitionFns } = useContext(PageTransitionContext)

  useEffect(() => {
    setTransitionFns({ enter, exit })

    return () => {
      setTransitionFns(defaultPageTransitionFns)
    }
  }, [enter, exit, setTransitionFns])

  return {
    enter,
    exit,
  }
}

export function useGlobalPageTransition({
  onBeforeEnter = defaultGlobalPageTransitionFns.onBeforeEnter,
  onAfterEnter = defaultGlobalPageTransitionFns.onAfterEnter,
  onBeforeExit = defaultGlobalPageTransitionFns.onBeforeExit,
  onAfterExit = defaultGlobalPageTransitionFns.onAfterExit,
  pages,
}: Partial<GlobalPageTransitionFns> & {
  pages?: string[]
}) {
  const { setGlobalTransitionFns, setGlobalTransitionPages } = useContext(
    PageTransitionContext
  )

  useEffect(() => {
    setGlobalTransitionFns({
      onBeforeEnter,
      onAfterEnter,
      onBeforeExit,
      onAfterExit,
    })
  }, [
    onBeforeEnter,
    onAfterEnter,
    onBeforeExit,
    onAfterExit,
    setGlobalTransitionFns,
  ])

  useEffect(() => {
    if (pages) {
      setGlobalTransitionPages(pages)
    }
  }, [pages, setGlobalTransitionPages])
}

export function usePageTransitionRefs(
  refs: Record<string, RefObject<unknown>>
) {
  const { elementRefs } = useContext(PageTransitionContext)

  useEffect(() => {
    for (const [key, ref] of Object.entries(refs)) {
      elementRefs.current[key] = ref
    }
  }, [refs, elementRefs])
}

export function useGlobalTransitionRefs(
  refs: Record<string, RefObject<unknown>>
) {
  const { globalElementRefs } = useContext(PageTransitionContext)
  const _pathname = usePathname()

  useEffect(() => {
    for (const [key, ref] of Object.entries(refs)) {
      globalElementRefs.current[key] = ref
    }
  }, [refs, globalElementRefs])
}

export function usePageTransitionNavigate() {
  const { navigate } = useContext(PageTransitionContext)

  return navigate
}
